import React from 'react';

interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  isLoading = false
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const clampedPage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = totalCount === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const endIndex = Math.min(clampedPage * pageSize, totalCount);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-slate-100 pt-4 mt-4">
      <div className="text-xs text-slate-500">
        Showing <span className="font-semibold text-slate-700">{startIndex}</span> to{' '}
        <span className="font-semibold text-slate-700">{endIndex}</span> of{' '}
        <span className="font-semibold text-slate-700">{totalCount}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange && (
          <select
            className="border rounded-lg px-2 py-1 text-xs bg-white text-slate-600"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => onPageChange(Math.max(1, clampedPage - 1))}
          disabled={isLoading || clampedPage <= 1}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span className="text-xs font-semibold text-slate-600">
          Page {clampedPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages, clampedPage + 1))}
          disabled={isLoading || clampedPage >= totalPages}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};
